























































import Vue from 'vue';
import {
  Component, Watch, Prop,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import { ValidationProduct, CycleQualifier, Qualifier } from '@/api/rewardsvalidation';

interface TableItem {
  cycle: string,
  cycleQualifier: CycleQualifier,
  children: TableItem[],
  expanded: boolean
}

@Component({ computed: mapState(['fiId']) })
export default class CycleValidationQualificationsTable extends Vue {
  @Prop() validationResult!: ValidationProduct;

  @Prop({ default: false }) machineLearningNotFound!: boolean;

  fiId!: string;

  headers: DataTableHeader[] = [];

  items: TableItem[] = [];

  missingQualRanges: string = '';

  rangesUnpopulated: boolean = false;

  readonly qualTypeFriendlyNameMap: Map<string, string> = new Map<string, string>([
    ['POS', 'POS Swipes'],
    ['ESTATEMENT', 'E-statement'],
    ['ACCOUNT_OPEN_CYCLE', 'Auto-Qualified'],
    ['ACH_CREDIT', 'ACH Credit'],
    ['ACH_DEBIT', 'ACH Debit'],
    ['BILL_PAY', 'Bill Pay'],
    ['ACH_DEBIT_OR_ACH_CREDIT', 'ACH Debit OR ACH Credit'],
    ['ACH_CREDIT_OR_BILLPAY', 'ACH Credit OR Bill Pay'],
    ['ACH_DEBIT_OR_BILLPAY', 'ACH Debit OR Bill Pay'],
    ['ACH_DEBIT_OR_ACH_CREDIT_OR_BILLPAY', 'ACH Credit OR ACH Debit OR Bill Pay'],
    ['IB_LOGIN', 'IB Login'],
    ['MINIMUM_BALANCE', 'Min. Balance'],
    ['LINKED_ACCOUNT', 'Linked Account'],
    ['OVERRIDE', 'Override'],
    ['CC_OR_POS', 'Credit Card or POS Swipes'],
    ['CC_COUNT', 'Credit Card (count)'],
    ['CC_SPEND', 'Credit Card (spend)'],
    ['CC_COUNT_OR_SPEND', 'Credit Card (count or spend)'],
    ['CC_COUNT_AND_SPEND', 'Credit Card (count and spend)'],
    ['LOAN', 'Loan'],
  ]);

  readonly INVALID_CLASS = 'invalidValue'

  readonly VALID_CLASS = 'validValue'

  readonly WARNING_CLASS = 'warningValue'

  mounted() {
    this.buildTable();
  }

  @Watch('validationResult')
  buildTable() {
    this.checkRanges();
    this.buildHeaders();
    this.buildItems();
  }

  checkRanges() {
    this.missingQualRanges = '';
    if (!this.machineLearningNotFound) {
      // Check if validation ranges have been populated for the current cycle.  If they have, check for any missing rewards.
      // If it looks like ranges are undefined, then it's a legacy snapshot and we fall back to using the validation flags.
      if (this.validationResult.cycleQualifiers.current.qualifiers.length > 0 && this.validationResult.cycleQualifiers.current.qualifiers[0].yellowThresholdMinimum === undefined) {
        this.rangesUnpopulated = true;
      } else {
        this.rangesUnpopulated = false;
        // Check for missing ranges.  Account open cycle will never be present, so ignore that one.
        this.validationResult.cycleQualifiers.current.qualifiers.forEach((qualifier: Qualifier) => {
          if (qualifier.qualType !== 'ACCOUNT_OPEN_CYCLE' && !qualifier.yellowThresholdMinimum) {
            let qualName: string | undefined = this.qualTypeFriendlyNameMap.get(qualifier.qualType);
            if (!qualName) {
              qualName = '?';
            }
            if (this.missingQualRanges.length > 0) {
              this.missingQualRanges += ', ';
            }
            this.missingQualRanges += qualName;
          }
        });
      }
    }
  }

  buildHeaders() {
    this.headers = [];

    if (this.validationResult) {
      const addedQualifiers = new Set<string>();
      this.buildHeadersForCycleQualifier(this.validationResult.cycleQualifiers.average, addedQualifiers);
      this.buildHeadersForCycleQualifier(this.validationResult.cycleQualifiers.current, addedQualifiers);
      this.buildHeadersForCycleQualifier(this.validationResult.cycleQualifiers.currentMinusOne, addedQualifiers);
      this.buildHeadersForCycleQualifier(this.validationResult.cycleQualifiers.currentMinusTwo, addedQualifiers);
      this.buildHeadersForCycleQualifier(this.validationResult.cycleQualifiers.currentMinusThree, addedQualifiers);

      this.headers.sort((a: DataTableHeader, b: DataTableHeader): number => a.text.localeCompare(b.text));

      this.headers.splice(0, 0, { text: '', value: 'toggle', width: '1px' });
      this.headers.splice(1, 0, { text: 'Cycle', value: 'cycle', width: '200px' });

      this.headers.push({ text: 'Overall Qualification', value: 'overallQualification', width: '200px' });
      this.headers.push({ text: 'Accounts Total', value: 'accountsTotal', width: '200px' });
      this.headers.push({ text: '', value: '' }); // Blank column to take up remaining table width
    }
  }

  buildHeadersForCycleQualifier(cycleQualifier: CycleQualifier | null, addedQualifiers: Set<string>) {
    if (cycleQualifier && cycleQualifier.qualifiers) {
      cycleQualifier.qualifiers.forEach((qualifier: Qualifier) => {
        if (!addedQualifiers.has(qualifier.qualType)) {
          const friendlyName: string | undefined = this.qualTypeFriendlyNameMap.get(qualifier.qualType);
          this.headers.push({
            text: friendlyName || '?',
            value: qualifier.qualType,
            width: '200px',
          });
          addedQualifiers.add(qualifier.qualType);
        }
      });
    }
  }

  async buildItems() {
    this.items = [];
    const newItems: TableItem[] = [];

    newItems.push({
      cycle: 'Current',
      cycleQualifier: this.validationResult.cycleQualifiers.current,
      children: [],
      expanded: false,
    });

    if (this.validationResult.cycleQualifiers.average) {
      const averageItem: TableItem = {
        cycle: '3-Month Average',
        cycleQualifier: this.validationResult.cycleQualifiers.average,
        children: [],
        expanded: false,
      };

      if (this.validationResult.cycleQualifiers.currentMinusOne) {
        averageItem.children.push(CycleValidationQualificationsTable.createItemForPreviousCycleQualifier(this.validationResult.cycleQualifiers.currentMinusOne));
      }

      if (this.validationResult.cycleQualifiers.currentMinusTwo) {
        averageItem.children.push(CycleValidationQualificationsTable.createItemForPreviousCycleQualifier(this.validationResult.cycleQualifiers.currentMinusTwo));
      }

      if (this.validationResult.cycleQualifiers.currentMinusThree) {
        averageItem.children.push(CycleValidationQualificationsTable.createItemForPreviousCycleQualifier(this.validationResult.cycleQualifiers.currentMinusThree));
      }

      newItems.push(averageItem);
    }

    this.items = newItems;
  }

  static createItemForPreviousCycleQualifier(cycleQualifier: CycleQualifier): TableItem {
    return {
      cycle: cycleQualifier.dropDate ? new Date(cycleQualifier.dropDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '',
      cycleQualifier,
      children: [],
      expanded: false,
    };
  }

  getClass(item: TableItem, header: DataTableHeader): string {
    let className = '';
    if (header.value !== 'cycle' && header.value !== 'accountsTotal') {
      className = this.VALID_CLASS;
      if (item.cycle === 'Current') {
        if (header.value === 'overallQualification') {
          className = this.getClassFromValidation(parseInt(item.cycleQualifier.totalAverage, 10), item.cycleQualifier.averageYellowThresholdMinimum, item.cycleQualifier.averageGreenThresholdMinimum,
            item.cycleQualifier.averageGreenThresholdMaximum, item.cycleQualifier.averageYellowThresholdMaximum, item.cycleQualifier.isTotalAverageValid);
        } else {
          const qualifier: Qualifier | null = CycleValidationQualificationsTable.getQualifier(item.cycleQualifier, header.value);
          if (qualifier) {
            className = this.getClassFromValidation(parseInt(qualifier.qualifiedPercentageDouble, 10), qualifier.yellowThresholdMinimum, qualifier.greenThresholdMinimum,
              qualifier.greenThresholdMaximum, qualifier.yellowThresholdMaximum, qualifier.isValid);
          }
        }
      }
    }

    return className;
  }

  // Thresholds will be undefined for legacy snapshots that did not support importing them from machine-learning.
  // Null for newer snapshots if the file could not be found.
  getClassFromValidation(value: number, yellowMin: number | null | undefined, greenMin: number | null | undefined, greenMax: number | null | undefined,
    yellowMax: number | null | undefined, legacyValidationFlag: boolean | undefined): string {
    let className = this.VALID_CLASS;
    if (yellowMin && greenMin && greenMax && yellowMax) {
      if (value < greenMin * 100) {
        className = value < yellowMin * 100 ? this.INVALID_CLASS : this.WARNING_CLASS;
      } else if (value > greenMax * 100) {
        className = value > yellowMax * 100 ? this.INVALID_CLASS : this.WARNING_CLASS;
      }
    } else if (this.rangesUnpopulated && legacyValidationFlag === false) {
      className = this.INVALID_CLASS;
    }

    return className;
  }

  getTableValue = (item: TableItem, header: DataTableHeader): string => {
    if (header.value === 'cycle') {
      return item.cycle;
    } if (header.value === 'overallQualification') {
      return `${item.cycleQualifier.totalAverage}%`;
    } if (header.value === 'accountsTotal') {
      return `${item.cycleQualifier.totalAccounts}`;
    }
    return CycleValidationQualificationsTable.getQualifierValue(item.cycleQualifier, header.value);
  }

  static getQualifierValue(cycleQualifier: CycleQualifier, qualType: string): string {
    let value = '';
    const qualifier: Qualifier | null = CycleValidationQualificationsTable.getQualifier(cycleQualifier, qualType);
    if (qualifier) {
      value = `${qualifier.qualifiedPercentage}%`;
    }

    return value;
  }

  static getQualifier(cycleQualifier: CycleQualifier, qualType: string): Qualifier | null {
    for (let i = 0; i < cycleQualifier.qualifiers.length; i++) {
      if (cycleQualifier.qualifiers[i].qualType === qualType) {
        return cycleQualifier.qualifiers[i];
      }
    }
    return null;
  }
}
